/**
 * PlanDetailAccordions — Mobile and Internet plan spec displays.
 * Matches web frontend `PlanDetailAccordions.tsx`.
 */
import { View, Text, StyleSheet } from 'react-native'
import { Check } from 'lucide-react-native'
import { colors } from '../theme'

// ─── Shared utility ──────────────────────────────

export const parseSpecialFeatures = (raw: any, alreadyShown: string[]): string[] => {
  const arr: string[] = Array.isArray(raw) ? raw : (typeof raw === 'string' && raw ? raw.split(',').map((s: string) => s.trim()).filter(Boolean) : [])
  const shownLower = alreadyShown.map(s => s.toLowerCase())
  return arr.filter(f => {
    const fl = f.toLowerCase()
    if (shownLower.some(s => fl.includes(s) || s.includes(fl))) return false
    if (f.length > 50) return false
    return true
  })
}

// ─── Mobile Detail ───────────────────────────────

export function MobileDetailAccordion({ details, showSpecs = false }: { details: Record<string, any>; showSpecs?: boolean }) {
  const callsSms = details?.calls_sms as string | undefined
  const euGb = details?.data_eu_gb
  const speed = details?.speed
  const creationFee = details?.creation_fee as number | undefined
  const hotspot = details?.hotspot_tethering

  const networkParts: string[] = []
  if (showSpecs) {
    if (details?.network) networkParts.push(details.network)
    if (speed && /^[345]G/i.test(speed)) networkParts.push(speed)
  }

  const roamingTerms = details?.roaming_terms as string | undefined

  const checks: string[] = []
  if (callsSms) checks.push(callsSms.charAt(0).toUpperCase() + callsSms.slice(1))
  if (euGb) checks.push(`${euGb} GB EU-data`)
  else if (details?.eu_data) checks.push('EU-data inkluderet')
  if (roamingTerms) {
    const rt = roamingTerms.toLowerCase()
    const isJustEuRepeat = euGb && rt.includes('eu') && rt.includes(String(euGb))
    if (!isJustEuRepeat) {
      if (rt.includes('verden') || rt.includes('world') || rt.includes('likehome')) {
        checks.push(roamingTerms)
      } else if (!rt.includes('kun dk') && !rt.includes('ingen eu')) {
        checks.push(roamingTerms)
      }
    }
  }
  if (speed && !/^[345]G/i.test(speed)) checks.push(`Hastighed: ${speed}`)
  if (hotspot === 'included' || hotspot === true) checks.push('Hotspot')
  if (showSpecs) {
    const binding = details?.contract_length
    if (binding === 'Ingen binding' || binding === '0 mdr' || binding === 0) checks.push('Ingen binding')
    else if (binding) checks.push(`Binding: ${binding}`)
  }
  if (creationFee === 0) checks.push('Gratis oprettelse')
  else if (creationFee != null) checks.push(`Oprettelse: ${creationFee} kr`)

  const alreadyShown = [
    String(details?.gb_dk || ''), details?.network || '', details?.contract_length || '',
    speed || '', String(euGb || ''), '5g', 'esim',
  ].filter(Boolean)
  const features = parseSpecialFeatures(details?.special_features, alreadyShown)

  const hasContent = networkParts.length > 0 || checks.length > 0 || features.length > 0
  if (!hasContent) return null

  return (
    <View style={s.container}>
      {networkParts.length > 0 && (
        <Text style={s.networkLine}>{networkParts.join(' · ')}</Text>
      )}
      {checks.length > 0 && (
        <View style={s.checkList}>
          {checks.map((item, i) => (
            <View key={i} style={s.checkRow}>
              <Check size={12} color="#16a34a" />
              <Text style={s.checkText}>{item}</Text>
            </View>
          ))}
        </View>
      )}
      {features.length > 0 && (
        <View style={s.featureWrap}>
          {features.map((f, i) => (
            <View key={i} style={[s.featurePill, { backgroundColor: '#eff6ff' }]}>
              <Text style={[s.featurePillText, { color: colors.primary }]}>{f}</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  )
}

// ─── Internet Detail ─────────────────────────────

export function InternetDetailAccordion({ details, showSpecs = false }: { details: Record<string, any>; showSpecs?: boolean }) {
  const download = details?.download_mbit
  const upload = details?.upload_mbit
  const creationFee = details?.creation_fee as number | undefined
  const routerIncluded = details?.router_included
  const routerModel = details?.router_model as string | undefined
  const cancellationNotice = details?.cancellation_notice
  const coverageArea = details?.coverage_area as string | undefined

  const techParts: string[] = []
  const speedShownInTech = showSpecs && (download || upload)
  if (showSpecs) {
    if (details?.technology) techParts.push(details.technology.charAt(0).toUpperCase() + details.technology.slice(1))
    if (download && upload && upload !== download) techParts.push(`${download}/${upload} Mbit/s`)
    else if (download) techParts.push(`${download} Mbit/s`)
  }
  const techLine = techParts.join(' · ')

  const cleanRouterText = (): string | null => {
    if (!routerIncluded) return null
    if (!routerModel) return 'Router inkluderet'
    const m = routerModel.toLowerCase()
    if (m.includes('byod') || m.includes('customer provides') || m.includes('sim card')) return null
    const cleaned = routerModel.replace(/\s*included\s*/gi, '').replace(/\s*inkluderet\s*/gi, '').replace(/\s*router\s*/gi, ' ').trim()
    if (cleaned.length > 30) return 'Router inkluderet'
    return cleaned ? `Router inkl. (${cleaned})` : 'Router inkluderet'
  }

  const getCoverageLabel = (): string | null => {
    if (!coverageArea) return null
    const a = coverageArea.toLowerCase()
    if (a.includes('hele danmark') && !a.includes('only') && !a.includes('kun')) return null
    if (a.includes('boligforening') || a.includes('housing association')) return 'Kun boligforeninger'
    const cityMatch = coverageArea.match(/^([\wæøåÆØÅ]+)\s/i)
    if (a.includes('aarhus') || a.includes('8000')) return 'Kun Aarhus-området'
    if (a.includes('regional') || a.includes('udvalgte')) return 'Regionalt begrænset'
    if (cityMatch) return `Kun ${cityMatch[1]}-området`
    return null
  }

  const checks: string[] = []
  if (upload && (!speedShownInTech || (download && upload !== download))) checks.push(`Upload: ${upload} Mbit/s`)
  const routerText = cleanRouterText()
  if (routerText) checks.push(routerText)
  if (showSpecs) {
    const binding = details?.contract_length
    const normalizeBinding = (b: any): string => {
      if (!b || b === 'Ingen binding' || b === '0 mdr' || b === 0) return 'Ingen binding'
      const str = String(b).toLowerCase()
      if (str.includes('boligforening')) return 'Boligforeningsaftale'
      const mdrMatch = str.match(/(\d+)\s*m(?:dr?|åneder?)\.?\s*(?:binding)?/i)
      if (mdrMatch) return `${mdrMatch[1]} mdr.`
      return String(b)
    }
    const bindingText = normalizeBinding(binding)
    checks.push(bindingText === 'Ingen binding' ? 'Ingen binding' : `Binding: ${bindingText}`)
  }
  if (creationFee === 0) checks.push('Gratis oprettelse')
  else if (creationFee != null) checks.push(`Oprettelse: ${creationFee} kr`)
  if (cancellationNotice) checks.push(`Opsigelse: ${cancellationNotice}`)
  const coverageLabel = getCoverageLabel()
  if (coverageLabel) checks.push(coverageLabel)

  const alreadyShown = [
    String(download || ''), String(upload || ''), details?.technology || '',
    details?.contract_length || '', 'mbit', 'router', 'wifi',
    coverageArea || '', 'oprettelse', 'binding',
  ].filter(Boolean)
  const rawFeatures = parseSpecialFeatures(details?.special_features, alreadyShown)
  const features = rawFeatures.filter(f => {
    if (/\b(included|available|only|customer|provides|response time|years? in business|CVR|NOT)\b/i.test(f)) return false
    if (/\d+\s*kr\/md/.test(f)) return false
    return true
  })

  const hasContent = !!techLine || checks.length > 0 || features.length > 0
  if (!hasContent) return null

  return (
    <View style={s.container}>
      {!!techLine && <Text style={s.networkLine}>{techLine}</Text>}
      {checks.length > 0 && (
        <View style={s.checkList}>
          {checks.map((item, i) => (
            <View key={i} style={s.checkRow}>
              <Check size={12} color="#16a34a" />
              <Text style={s.checkText}>{item}</Text>
            </View>
          ))}
        </View>
      )}
      {features.length > 0 && (
        <View style={s.featureWrap}>
          {features.map((f, i) => (
            <View key={i} style={[s.featurePill, { backgroundColor: '#f3e8ff' }]}>
              <Text style={[s.featurePillText, { color: '#7c3aed' }]}>{f}</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  )
}

const s = StyleSheet.create({
  container: { gap: 6 },
  networkLine: { fontSize: 11, color: '#64748b', fontWeight: '500' },
  checkList: { gap: 3 },
  checkRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  checkText: { fontSize: 12, color: '#475569' },
  featureWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, paddingTop: 2 },
  featurePill: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12 },
  featurePillText: { fontSize: 10, fontWeight: '500' },
})
